import { escapeHtml } from "./escapeHtml";
// Monthly Profit Trend report: rows built from the shared, tested monthly totals in aiCharts.js
// (exact cents, month taken from the date text, same revenue and cost definitions as the job totals).

import { monthlyTotalsForAI, undatedTotals, formatMoney } from "./aiCharts";

const SHORT_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// "2026-03" -> "Mar '26". Plain text only, no Date, so no time zone can shift it. Bad input -> "".
export function monthLabel(ym) {
  const m = typeof ym === "string" ? ym.match(/^(\d{4})-(0[1-9]|1[0-2])/) : null;
  return m ? `${SHORT_MONTHS[parseInt(m[2], 10) - 1]} '${m[1].slice(-2)}` : "";
}

// Rows in the shape the report's chart, table and exports use.
export function monthlyReportRows(jobs) {
  return monthlyTotalsForAI(jobs).map((r) => ({
    month: monthLabel(r.month),
    date: `${r.month}-01`,
    revenue: r.revenue,
    costs: r.costs,
    profit: r.profit,
  }));
}

// Plain note about money that has no date. Empty text when nothing is left out.
export function monthlyReportNote(jobs) {
  const { costCents, revCents } = undatedTotals(jobs);
  const notes = [];
  if (costCents !== 0) notes.push(`Leaves out ${formatMoney(costCents / 100)} of costs that have no date.`);
  if (revCents !== 0) notes.push(`Leaves out ${formatMoney(revCents / 100)} of revenue that has no date.`);
  return notes.join(" ");
}

// Same wording as before for two or more months; safe with one month or none.
export function monthlyInsight(data, money) {
  const rows = Array.isArray(data) ? data : [];
  const recent = rows[rows.length - 1];
  if (!recent) return "There are no dated months to show yet.";
  const prev = rows[rows.length - 2];
  if (!prev) return `Your most recent month shows ${money(recent.profit)} profit. Add another month of activity to see how it compares.`;
  const change = recent.profit - prev.profit;
  return `Your most recent month shows ${money(recent.profit)} profit — ${change >= 0 ? "up" : "down"} ${money(Math.abs(change))} from the prior month. ${change >= 0 ? "Positive momentum — keep monitoring costs as revenue grows." : "Review which jobs closed that month and whether cost overruns were the driver."}`;
}

// escapeHtml lives in its own module; re-exported so existing imports keep working.
export { escapeHtml };

// The PDF block for the note. Empty string when there is no note, so the page is unchanged.
export function pdfNoteHTML(note) {
  return note ? `<div class="note">${escapeHtml(note)}</div>` : "";
}

// The Excel row for the note (one cell, padded to the table width), or no rows when there is no note.
export function excelNoteRows(note, columnCount) {
  return note ? [[note, ...Array(Math.max(columnCount - 1, 0)).fill("")]] : [];
}
